// The portal's unread badges. The SERVER owns the counts
// (GET /patient-app/messaging/unread-summary); these rules only read them.

export const INCOMING_MESSAGE_ACTIONS = Object.freeze({
  IGNORE: "ignore",
  MARK_READ: "mark-read",
  REFETCH: "refetch",
});

/** `Map<conversationId, count>` of the positive counts in a summary; malformed rows are dropped. */
export function unreadMapFrom(summary) {
  const map = new Map();
  const rows = Array.isArray(summary?.byConversation) ? summary.byConversation : [];
  for (const row of rows) {
    if (typeof row?.conversationId !== "string" || !row.conversationId) continue;
    if (!Number.isInteger(row.unreadCount) || row.unreadCount <= 0) continue;
    map.set(row.conversationId, row.unreadCount);
  }
  return map;
}

/** A row's badge. The open thread is being read, so it never shows a count. */
export function unreadCountFor(map, conversationId, openConversationId) {
  if (!conversationId || conversationId === openConversationId) return 0;
  return map?.get(conversationId) || 0;
}

/** The summary as it will be once `conversationId` is read (the optimistic cache edit). */
export function withoutConversation(summary, conversationId) {
  const rows = Array.isArray(summary?.byConversation) ? summary.byConversation : [];
  const byConversation = rows.filter((row) => row?.conversationId !== conversationId);
  return {
    ...summary,
    byConversation,
    totalUnread: byConversation.reduce(
      (sum, row) => sum + (Number.isFinite(row?.unreadCount) ? row.unreadCount : 0),
      0
    ),
    conversationsWithUnread: byConversation.length,
  };
}

export function formatBadgeCount(count) {
  return count > 99 ? "99+" : String(count);
}

/**
 * Whether the nav badge may exist at all. Never while a dependent is on
 * screen (the Messaging item is not offered there, and a hidden item must not
 * poll), never when the clinic turned the Messaging tab off, never without a
 * session. Mirrors Layout.js's own `portalTabs[key] !== false` rule.
 */
export function isMessagingBadgeEnabled(state) {
  if (state?.viewingDependent) return false;
  if (!state?.jwt || !state?.patient?.pid) return false;
  return state?.organization?.patientPortalTabs?.messaging !== false;
}

/**
 * What a `message` socket event means for the badges: the patient's own
 * message changes nothing; a message into the thread they are looking at is
 * read on arrival; anything else means the counts moved.
 */
export function incomingMessageAction({messageData, patientId, openConversationId, isVisible}) {
  if (!messageData || typeof messageData.conversationId !== "string") {
    return INCOMING_MESSAGE_ACTIONS.IGNORE;
  }
  if (messageData.senderId === patientId) return INCOMING_MESSAGE_ACTIONS.IGNORE;
  if (isVisible && messageData.conversationId === openConversationId) {
    return INCOMING_MESSAGE_ACTIONS.MARK_READ;
  }
  return INCOMING_MESSAGE_ACTIONS.REFETCH;
}
